'use client';

import { type ReactElement, useMemo } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { type PolymorphicComponentProps } from '../../types/component';
import { type ResponsiveOption } from '../../types/responsiveOption';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';
import {
  ListContext,
  type ListContextValue,
  useListContext,
} from './ListContext';

const listClasses = { root: 'zds-list-root' } as const;

const listStyles = tv({
  // overflow-hidden + rounded-[inherit] clips item backgrounds to the
  // parent's border-radius (e.g. Surface), preventing corner bleed.
  base: [
    listClasses.root,
    'm-0 list-none overflow-hidden rounded-[inherit] p-0',
  ],
  variants: {
    density: {
      compact: 'py-0.5',
      default: 'py-1',
      spacious: 'py-1.5',
    },
    densityXs: {
      compact: 'xs:py-0.5',
      default: 'xs:py-1',
      spacious: 'xs:py-1.5',
    },
    densitySm: {
      compact: 'sm:py-0.5',
      default: 'sm:py-1',
      spacious: 'sm:py-1.5',
    },
    densityMd: {
      compact: 'md:py-0.5',
      default: 'md:py-1',
      spacious: 'md:py-1.5',
    },
    densityLg: {
      compact: 'lg:py-0.5',
      default: 'lg:py-1',
      spacious: 'lg:py-1.5',
    },
    densityXl: {
      compact: 'xl:py-0.5',
      default: 'xl:py-1',
      spacious: 'xl:py-1.5',
    },
  },
});

const ELEMENT_TYPE = 'ul';

type ListElement = 'ol' | typeof ELEMENT_TYPE;

type Variant = VariantProps<typeof listStyles>;

interface ListBaseProps {
  /**
   * Spacing density. Accepts a static value or a responsive object
   * (e.g. `{ default: 'default', sm: 'compact' }`). Propagates to children via context.
   * @default 'default'
   */
  density?: ResponsiveOption<NonNullable<Variant['density']>>;
  /**
   * Colour scheme. Propagates to children via context.
   * @default 'neutral'
   */
  color?: 'neutral' | 'static-white' | 'accent-primary';
  /**
   * Vertical alignment of row content. Propagates to children via context.
   * @default 'center'
   */
  alignItems?: 'top' | 'center';
  /**
   * Corner shape of items. Propagates to children via context.
   * @default 'square'
   */
  itemShape?: 'square' | 'rounded';
  /**
   * Remove default vertical padding from the list root.
   * @default false
   */
  disablePadding?: boolean;
}

type ListProps<T extends ListElement = typeof ELEMENT_TYPE> = Omit<
  PolymorphicComponentProps<ListBaseProps, T>,
  'component'
> & {
  /**
   * The list element to render (`'ul'` or `'ol'`).
   * @default 'ul'
   */
  component?: T;
};

interface ListComponent {
  <T extends ListElement = typeof ELEMENT_TYPE>(
    props: ListProps<T>
  ): ReactElement | null;
  displayName?: string;
}

const ListRoot: ListComponent = (props) => {
  const {
    children,
    component: Root = ELEMENT_TYPE,
    density: densityProp,
    color: colorProp,
    alignItems: alignItemsProp,
    itemShape: itemShapeProp,
    disablePadding = false,
    className,
    ref,
    ...rest
  } = props;

  const parentContext = useListContext();

  const { defaultOption: defaultDensity, responsiveOption: responsiveDensity } =
    resolveResponsiveProps(densityProp);
  const density = defaultDensity ?? parentContext?.density ?? 'default';
  const color = colorProp ?? parentContext?.color ?? 'neutral';
  const alignItems = alignItemsProp ?? parentContext?.alignItems ?? 'center';
  const shape = itemShapeProp ?? parentContext?.shape ?? 'square';

  const context = useMemo<ListContextValue>(
    () => ({
      density,
      responsiveDensity: responsiveDensity ?? parentContext?.responsiveDensity,
      color,
      alignItems,
      shape,
    }),
    [
      density,
      responsiveDensity,
      parentContext?.responsiveDensity,
      color,
      alignItems,
      shape,
    ]
  );

  return (
    <ListContext.Provider value={context}>
      <Root
        {...rest}
        ref={ref as React.Ref<never>}
        // Safari VoiceOver strips implicit list semantics when list-style: none is applied.
        // https://bugs.webkit.org/show_bug.cgi?id=170179
        role="list"
        className={listStyles({
          density: disablePadding ? undefined : density,
          densityXs: disablePadding
            ? undefined
            : (responsiveDensity ?? parentContext?.responsiveDensity)?.xs,
          densitySm: disablePadding
            ? undefined
            : (responsiveDensity ?? parentContext?.responsiveDensity)?.sm,
          densityMd: disablePadding
            ? undefined
            : (responsiveDensity ?? parentContext?.responsiveDensity)?.md,
          densityLg: disablePadding
            ? undefined
            : (responsiveDensity ?? parentContext?.responsiveDensity)?.lg,
          densityXl: disablePadding
            ? undefined
            : (responsiveDensity ?? parentContext?.responsiveDensity)?.xl,
          className,
        })}
      >
        {children}
      </Root>
    </ListContext.Provider>
  );
};

ListRoot.displayName = 'List';

export {
  ListRoot,
  type ListProps,
  type ListBaseProps,
  listClasses,
  // @internal — exported so TS can name ListComponent in the declaration file
  // for index.ts. Do not re-export from barrel files.
  type ListComponent,
};
